import { Mui } from "@osu/react-ui";
import { getApplDetailParams, humanizeEnum } from "../../util/functions";
import { DECISION_LABELS } from "../../util/constants";
import {
    useGetScholarsApplicantQueueEntriesQuery,
    useGetScholarsProgramsQuery,
} from "../../services/graphql/api";
import { useApplicantDetailsState } from "../hooks/useApplicantDetailsState";
import ScholarsDecisionPanel from "./ScholarsDecisionPanel";
import ScholarsOptInAdmitPanel from "./ScholarsOptInAdmitPanel";

// Terminal Scholars outcomes (U6/U7). When one of these is set, it takes priority over the queue-based
// review messages — an admitted/finally-denied applicant should show their outcome, not a live panel.
const OUTCOME_ADMITTED = "SCH_ADMITTED";
const OUTCOME_FINAL_DENY = "SCH_FINAL_DENY";
// Opt-in (alternate) applicants exhausted their preferences with no offers and opted in. They have NO
// active queue entry, so the queue-based block below would otherwise show "completed the review process".
const OUTCOME_ALTERNATE_PENDING = "ALTERNATE_PENDING";

/**
 * has507 — Scholars Recommendation section for the scholars applicant details page.
 * Priority: terminal outcome (admitted / final deny) > active review (IN_REVIEW) > recommended
 * (awaiting approval) > queue status summary.
 */
export default function ScholarsRecommendation() {
    const { applicantEmployeeId, term } = getApplDetailParams();
    const { data: queueEntries, isLoading } = useGetScholarsApplicantQueueEntriesQuery(
        { applicantEmployeeId, term },
        { skip: !applicantEmployeeId }
    );
    const { data: programs } = useGetScholarsProgramsQuery();
    // Terminal outcome rides along on the cached application-details blob (administratorDetails).
    const { data: appDetails } = useApplicantDetailsState();
    const outcomeStatus = appDetails?.details?.administratorDetails?.scholarsOutcomeStatus;
    const admittedSubProgramId = appDetails?.details?.administratorDetails?.scholarsAdmittedSubProgramId;

    if (isLoading) {
        return <Mui.Skeleton variant="rectangular" height={80} />;
    }

    // Build program label lookup (used by both the outcome display and the queue displays).
    const programLabels = (programs ?? []).reduce((acc, p) => {
        acc[p.subProgramId] = p.label || p.subProgramId;
        return acc;
    }, {});

    // 1. Terminal outcome takes priority (e.g. alternate admits, final denies). Read-only.
    if (outcomeStatus === OUTCOME_ADMITTED) {
        const label = programLabels[admittedSubProgramId] || admittedSubProgramId;
        return (
            <Mui.Box sx={{ p: 1 }} data-testid="scholars-outcome-admitted">
                <Mui.Alert severity="success">
                    Admitted to <strong>{label}</strong>.
                </Mui.Alert>
            </Mui.Box>
        );
    }
    if (outcomeStatus === OUTCOME_FINAL_DENY) {
        return (
            <Mui.Box sx={{ p: 1 }} data-testid="scholars-outcome-final-deny">
                <Mui.Alert severity="info">
                    Final decision: <strong>Denied</strong>.
                </Mui.Alert>
            </Mui.Box>
        );
    }

    // has552 — Opt-in (ALTERNATE_PENDING) applicant: they have no active queue entry, so the queue-based
    // block below would show "completed the review process" for everyone. A Program Manager needs to admit
    // them into their own program from here (admin stays read-only — handled inside the panel).
    if (outcomeStatus === OUTCOME_ALTERNATE_PENDING) {
        return (
            <Mui.Box sx={{ p: 1 }}>
                <ScholarsOptInAdmitPanel applicantEmployeeId={applicantEmployeeId} />
            </Mui.Box>
        );
    }

    if (!queueEntries || queueEntries.length === 0) {
        return (
            <Mui.Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                This applicant has not been released to review yet.
            </Mui.Typography>
        );
    }

    // Find the active queue entry (IN_REVIEW = actionable for a decision)
    const activeEntry = queueEntries.find(e => e.status === "IN_REVIEW");
    // A decided entry (RECOMMENDED) shows read-only, awaiting admin approval.
    const recommendedEntry = queueEntries.find(e => e.status === "RECOMMENDED");

    return (
        <Mui.Box sx={{ p: 1 }}>
            {activeEntry ? (
                <ScholarsDecisionPanel
                    applicantEmployeeId={applicantEmployeeId}
                    subProgramId={activeEntry.subProgramId}
                    term={term}
                    programName={programLabels[activeEntry.subProgramId] || activeEntry.subProgramId}
                />
            ) : recommendedEntry ? (
                // Decided — read-only decision, awaiting admin approval (mirrors Honors read-only state).
                <Mui.Box sx={{ mt: 1 }} data-testid="scholars-decision-readonly">
                    <Mui.Typography>
                        Decision: <strong>{decisionLabel(recommendedEntry.recommendation)}</strong>
                        {' '}({programLabels[recommendedEntry.subProgramId] || recommendedEntry.subProgramId}) — awaiting admin approval.
                    </Mui.Typography>
                </Mui.Box>
            ) : (
                <Mui.Alert severity="info" sx={{ mt: 1 }}>
                    {queueEntries.some(e => e.status === "QUEUED")
                        ? "This applicant is queued and awaiting reviewer assignment."
                        : "This applicant has completed the review process."
                    }
                </Mui.Alert>
            )}
        </Mui.Box>
    );
}

// Map the backend decision enum to a friendly label for read-only display.
function decisionLabel(recommendation) {
    return humanizeEnum(recommendation, DECISION_LABELS) || "—";
}
